import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';

import {
  requestInsert,
  requestUpdate,
  requestDelete,
  requestInform as request,
  requestCommentDelete
} from 'api/board/qna';
import Divider from 'components/common/Divider';
import Content from 'components/common-sfims/Content';
import Editor from 'components/common-sfims/form/Editor';
import Comment from 'components/pages/board/qna/Comment';
import { onEnterKeyPress, onInputChange } from 'function/event';
import { utc2tz } from 'function/convert';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import { RETURN_YN } from 'types/type';
import format from 'utils/string/format';

/**
 * 협업 게시판 > 질문 및 답변 - 등록/수정/조회
 */
const Inform = () => {
  /* variable */
  const { seq: loginSeq, adminYn } = getLoginInform();

  /* router */
  const history = useHistory();
  const { pathname, state } = useLocation();
  const { seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // 답변 모달에 전달할 데이터
  const [commentModalProps, setCommentModalProps] = useObjReducer({});
  // 답변 목록
  const [comments, setComments] = useState([]);
  // 질문 및 답변 정보
  const [inform, setInform] = useObjReducer({});
  // 삭제 비활성화 여부
  const [isDisableDelete, setIsDisableDelete] = useState(false);
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 질문 등록/수정 요청 데이터
  const [questionData, setQuestionData] = useObjReducer({
    title: '',
    content: ''
  });

  /* watch */
  useEffect(() => {
    // 수정/조회 화면인 경우 질문 및 답변 정보 조회 요청
    if (seq) {
      requestInform();
    }
  }, []);

  /* method */
  // 질문 및 답변 정보 조회 요청
  const requestInform = async () => {
    try {
      const { data = {}, code, message } = await request(seq);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      // 수정 화면인 경우 권한 유효성 확인
      if (
        pathname.includes('setting') &&
        seq &&
        adminYn !== RETURN_YN.Y &&
        loginSeq !== data.regUsr
      ) {
        // 권한이 없는 경우
        throw { code: RESPONSE_CODE.Forbidden };
      }

      setComments(data.qnaComments ?? []);
      setInform(data);
      setQuestionData({
        title: data.title ?? '',
        content: data.content ?? ''
      });
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 목록 화면 표시
      showList();
    }
  };
  // 목록 화면 표시
  const showList = () => {
    history.push(ROUTER_PATH.board.qna.list, state);
  };

  /* event */
  // 삭제 클릭 시
  const onDeleteClick = () => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        setIsDisableDelete(true);

        try {
          const { code, message } = await requestDelete(seq);

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 목록 화면 표시
          showList();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
          setIsDisableDelete(false);
        }
      }
    });
  };
  // 답변 삭제 클릭 시
  const onDeleteCommentClick = seq => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        try {
          const { code, message } = await requestCommentDelete(seq);

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 질문 및 답변 정보 조회 요청
          await requestInform();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
        }
      }
    });
  };
  // 설정
  const onSubmit = async event => {
    event.preventDefault();

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(questionData)) {
        throw {
          code: RESPONSE_CODE[seq ? 'BadRequestUpdate' : 'BadRequestInsert']
        };
      }

      if (seq) {
        const { code, message } = await requestUpdate(seq, questionData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      } else {
        const { code, message } = await requestInsert(questionData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      }

      // 목록 화면 표시
      showList();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <>
      <Form onSubmit={onSubmit}>
        <Content className={{ body: 'bg-light' }}>
          {pathname.includes('setting') && (
            <Fragment key="title">
              {getText(seq ? 'update' : 'insert')}
            </Fragment>
          )}
          <Fragment key="body">
            <Form.Group className="mb-3">
              <Form.Label>
                {pathname.includes('setting') && '* '}
                {getText('title')}
              </Form.Label>
              {/* view일 경우 readOnly 삽입 */}
              <Form.Control
                readOnly={!pathname.includes('setting')}
                size="sm"
                type="text"
                value={questionData.title}
                autoFocus={pathname.includes('setting')}
                maxLength={100}
                name="title"
                placeholder={getText('boardQnATitlePlaceholder')}
                required
                onChange={event => {
                  if (pathname.includes('setting')) {
                    onInputChange(event, setQuestionData);
                  }
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {pathname.includes('setting') && '* '}
                {getText('content')}
              </Form.Label>
              {pathname.includes('setting') ? (
                <Editor
                  value={questionData.content}
                  onUpdate={(content = '') => setQuestionData({ content })}
                />
              ) : (
                <>
                  {/* view일 경우 */}
                  <div
                    className="p-3 bg-white"
                    dangerouslySetInnerHTML={{ __html: inform.content }}
                  />
                </>
              )}
            </Form.Group>
            {!pathname.includes('setting') && (
              <Form.Group>
                <Form.Label>{getText('answer')}</Form.Label>
                {comments.map((value = {}, index) => (
                  <Fragment key={`comments${value.seq}`}>
                    {index > 0 && <Divider />}
                    <div className="mb-3">
                      <p className="fs--1 mb-1 bg-200 rounded-3 p-2">
                        <strong className="me-2">
                          {format(getText('commentRegUsrNm'), {
                            regUsrNm: value.regUsrNm
                          })}
                        </strong>
                        <span>
                          {(adminYn === RETURN_YN.Y ||
                            loginSeq === value.regUsr) && (
                            <>
                              <a
                                href="#!"
                                onClick={() => {
                                  setCommentModalProps({
                                    show: true,
                                    inform: value
                                  });
                                }}
                              >
                                {getText('update')}
                              </a>{' '}
                              •{' '}
                              <a
                                href="#!"
                                onClick={() => onDeleteCommentClick(value.seq)}
                              >
                                {getText('delete')}
                              </a>{' '}
                              •{' '}
                            </>
                          )}
                          {utc2tz(value.regDt)}
                        </span>
                      </p>
                      <div
                        className="p-3 bg-white"
                        dangerouslySetInnerHTML={{ __html: value.comment }}
                      />
                    </div>
                  </Fragment>
                ))}
              </Form.Group>
            )}
          </Fragment>
          <Fragment key="footerButton">
            <Button
              size="sm"
              variant="secondary"
              className="ms-auto"
              onClick={showList}
            >
              {getText(pathname.includes('setting') ? 'cancel' : 'list')}
            </Button>
            {pathname.includes('setting') ? (
              <Button
                disabled={isDisableSetting}
                size="sm"
                type="submit"
                className="ms-2"
              >
                {getText('ok')}
              </Button>
            ) : (
              <>
                {!pathname.includes('setting') &&
                  seq &&
                  adminYn === RETURN_YN.Y && (
                    <Button
                      size="sm"
                      className="ms-2"
                      onClick={() => {
                        setCommentModalProps({
                          show: true,
                          inform: {},
                          seq
                        });
                      }}
                    >
                      {getText('insertComment')}
                    </Button>
                  )}
                {(adminYn === RETURN_YN.Y || loginSeq === inform.regUsr) && (
                  <>
                    <Button
                      size="sm"
                      variant="success"
                      className="ms-2"
                      onClick={() => {
                        history.push(
                          format(ROUTER_PATH.board.qna.setting, { seq }),
                          state
                        );
                      }}
                    >
                      {getText('update')}
                    </Button>
                    <Button
                      disabled={isDisableDelete}
                      size="sm"
                      variant="danger"
                      className="ms-2"
                      onClick={onDeleteClick}
                    >
                      {getText('delete')}
                    </Button>
                  </>
                )}
              </>
            )}
          </Fragment>
        </Content>
      </Form>
      <Comment
        {...commentModalProps}
        onHide={() => setCommentModalProps({ show: false })}
        onSetting={requestInform}
      />
    </>
  );
};

export default Inform;
